import { Injectable } from '@angular/core';

import { ParsingComponentConfig } from 'app/question/components/question/parsing-component-config';

@Injectable({
  providedIn: 'root'
})
export class QuestionTextParserService {
  private readonly tags: Record<string, ParsingComponentConfig>;

  public constructor() {
    this.tags = this.generateTagsDictionary();
  }

  public parseText(text: string): string {
    text = text.replace(/\s*=\s*"/g, '="');

    for (const tag of Object.keys(this.tags)) {
      text = this.updateComponentSyntax(text, this.tags[tag]);
    }

    return text;
  }

  public replaceQuestionText(questionTextRendered: string): string {
    const replaceRegex = /(<kh-manipulative)/gi;

    return questionTextRendered.replace(replaceRegex, '$1 report-mode="question"').replace(/ng-model="[^"]*"/, '');
  }

  private updateComponentSyntax(text: string, tagInfo: ParsingComponentConfig): string {
    if (tagInfo.startTagRegExp.test(text)) {
      return this.getComponentCodeText(tagInfo, text, tagInfo.parserFn);
    }

    return text;
  }

  private getComponentCodeText(tag: ParsingComponentConfig, text: string, transform: (text: string) => string): string {
    let buffer = text;
    const ret: string[] = [];
    while (tag.startTagRegExp.test(buffer)) {
      const openIndex = buffer.search(tag.startTagRegExp);
      const closeTagInfo = buffer.match(tag.endTagRegExp);

      if (!closeTagInfo) {
        ret.push(transform(buffer.substring(openIndex)));
        buffer = '';
        break;
      }

      const endIndex = (closeTagInfo.index ?? 0) + closeTagInfo[0].length;
      ret.push(buffer.substring(0, openIndex));
      ret.push(transform(buffer.substring(openIndex, endIndex)));
      buffer = buffer.substring(endIndex);
    }

    return ret.join('') + buffer;
  }

  private parseSpotlight(original: string): string {
    let text = original.replace('ng-model="', '[value]="');
    text = text.replace('>', ' (valueChange)="answerChanged($event)" >');
    text = text.replace(/spotlight-width="/g, '[spotlightWidth]="');
    text = text.replace(/tick-step-size="/g, '[tickStepSize]="');
    text = text.replace(/label-ticks="/g, '[labelTicks]="');
    text = text.replace(/time-step="/g, '[timeStep]="');
    text = text.replace(/major-jumps="/g, '[majorJumps]="');
    text = text.replace(/reduce-ticks="/g, '[reduceTicks]="');
    text = text.replace(/reduce-hints="/g, '[reduceHints]="');
    text = text.replace(/reduce-endpoints="/g, '[reduceEndpoints]="');
    text = text.replace(/reduce-target="/g, '[reduceTarget]="');
    text = text.replace(/reduce-target="/g, '[reduceTarget]="');
    text = text.replace(/input-type="/g, '[inputType]="');
    text = text.replace(/max-digits="/g, '[maxDigits]="');
    text = text.replace(/label-jumps="/g, '[labelJumps]="');
    text = text.replace(/jump-start="/g, '[jumpStart]="');

    return text;
  }

  private parseEquivFractions(original: string): string {
    const outputs: string[] = ['(submitAnswer)="submit($event)"'];

    let text = original.replace(/numerator="/, '[numerator]="');
    outputs.push('(numeratorChange)="answerChanged($event)"');

    text = text.replace(/targetnumrows="/g, '[targetNumRows]="');
    text = text.replace(/targetnumcols="/g, '[targetNumCols]="');
    text = text.replace(/targetnumerator="/g, '[targetNumerator]="');
    text = text.replace(/targetcolour="/g, 'targetColour="');
    text = text.replace(/usernumrows="/g, '[userNumRows]="');
    text = text.replace(/usernumcols="/g, '[userNumCols]="');
    text = text.replace(/usercolour="/g, 'userColour="');
    text = text.replace(/ishoriz="/g, '[isHoriz]="');

    outputs.push('(valueChange)="answerChanged($event)"');

    // @Input `height` left like it is

    text = text.replace('>', ` ${outputs.join(' ')} >`); // add output

    return text;
  }

  private parseIntegerInput(original: string): string {
    const outputs: string[] = ['(submitAnswer)="submit($event)"'];

    let text = original.replace(/itemindex="/g, '[itemIndex]="');
    outputs.push('(itemIndexChange)="manipItemIndexChanged($event)"');

    text = text.replace(/minvalue="/g, '[minValue]="');
    text = text.replace(/maxvalue="/g, '[maxValue]="');
    text = text.replace(/hideinput="/g, '[hideInput]="');
    text = text.replace(/defaultvalue="/g, 'defaultValue="');
    text = text.replace(/reversebuttonorder="/g, '[reverseButtonOrder]="');
    text = text.replace(/uplabel="/g, 'upLabel="');
    text = text.replace(/downlabel="/g, 'downLabel="');

    text = text.replace(/value="/, '[value]="');
    outputs.push('(valueChange)="answerChanged($event)"');

    // @Input `values` left like they are

    text = text.replace('>', ` ${outputs.join(' ')} >`); // add output

    return text;
  }

  private parseImageSwap(original: string): string {
    let text = original.replace(/itemindex="/g, '[itemIndex]="');
    text = text.replace('>', ' (itemIndexChange)="manipItemIndexChanged($event)" >'); // add output
    text = text.replace(/imagesource="/g, 'imageSource="');

    return text;
  }

  private parseMultiply(original: string): string {
    const outputs: string[] = ['(submitAnswer)="submit($event)"'];
    let text = original.replace(/imagesource="/g, 'imageSource="');
    text = text.replace(/multipliercolour="/g, 'multiplierColour="');
    text = text.replace(/minvalue="/g, '[minValue]="');
    text = text.replace(/maxvalue="/g, '[maxValue]="');
    text = text.replace(/defaultvalue="/g, '[defaultValue]="');
    text = text.replace(/minheight="/g, '[minHeight]="');

    text = text.replace(/(product|multiplier|value)="/g, '[$1]="');
    outputs.push('(valueChange)="answerChanged($event)"');

    text = text.replace('>', ` ${outputs.join(' ')} >`); // add output

    return text;
  }

  private parseDotConnect(original: string): string {
    const outputs: string[] = ['(submitAnswer)="submit($event)"'];
    let text = original.replace('ng-model="', '[value]="');
    text = text.replace(/background-image="/g, '[backgroundImage]="');
    text = text.replace(/dot-color="/g, '[dotColor]="');
    text = text.replace(/line-color="/g, '[lineColor]="');
    text = text.replace(/dot-size="/g, '[dotSize]="');
    text = text.replace(/\s(width|height)="/g, ' [$1]="');
    outputs.push('(valueChange)="answerChanged($event)"');
    text = text.replace('>', ` ${outputs.join(' ')} >`); // add output
    text = this.parseNode(text);

    return text;
  }

  private parseNode(original: string): string {
    let text = original.replace(/<\s*node/g, '<kh-node');
    text = text.replace(/<\s*\/\s*node\s*>/g, '</kh-node>');
    return text;
  }

  private parseImageZoom(original: string): string {
    let text = original.replace(/background-color="/g, '[backgroundColor]="');
    text = text.replace(/\s(width|height|viewbox|image)="/g, ' [$1]="');

    return text;
  }

  private parseAlgebraTiles(original: string): string {
    const outputs: string[] = [
      '(expressionChange)="answerChanged($event)"',
      '(submitAnswer)="submit($event)"'
    ];

    let text = original.replace(/expression="/g, '[expression]="');
    text = text.replace(/displaysummary="/g, '[displaySummary]="');

    text = text.replace('>', ` ${outputs.join(' ')} >`); // add outputs

    return text;
  }

  private parseFractionStrips(original: string): string {
    let text = original.replace(/showfraction="/g, '[showFraction]="');
    text = text.replace(/targetline="/g, '[targetLine]="');
    text = text.replace(/(sum|fraction|value)="/g, '[$1]="');

    text = text.replace('>', ' (fractionChange)="answerChanged($event)" (submitAnswer)="submit($event)"  >'); // add output

    return text;
  }

  private parseVideo(original: string): string {
    let text = original.replace(/videotype="/g, 'videoType="');
    text = text.replace(/videoid="/g, 'videoId="');
    text = text.replace(/hideratings="/g, 'hideRatings="');

    return text;
  }

  private parseAddingBlocks(original: string): string {
    let text = original.replace(/value="/g, '[value]="');
    text = text.replace('>', ' (valueChange)="answerChanged($event)" (submitAnswer)="submit($event)"  >'); // add output

    return text;
  }

  private parseSubtractingBlocks(original: string): string {
    let text = original.replace(/(value|term1|term2)="/g, '[$1]="');
    text = text.replace('>', ' (valueChange)="answerChanged($event)" (submitAnswer)="submit($event)"  >'); // add output

    return text;
  }

  private parseStringInput(original: string): string {
    let text = original.replace(/(frame|type|name)="/g, '[$1]="');
    text = text.replace(/font-family=/g, '[fontFamily]=');
    text = text.replace(/text-size=/g, '[textSize]=');
    text = text.replace(/max-length=/g, '[maxLength]=');
    text = text.replace(/border-radius=/g, '[borderRadius]=');
    text = text.replace(/border-stroke-size=/g, '[borderStrokeSize]=');
    text = text.replace(/background-color=/g, '[backgroundColor]=');
    text = text.replace(/text-alignment=/g, '[textAlignment]=');
    text = text.replace(/font-style=/g, '[fontStyle]=');
    text = text.replace(/text-style=/g, '[fontStyle]=');
    text = text.replace(/id=/g, '[id]=');

    return text;
  }

  private parseRepresentingNumbers(original: string): string {
    let text = original.replace('ng-model="', '[value]="');
    text = text.replace('>', ' (valueChange)="answerChanged($event)" >');
    text = text.replace(/feed-back=/g, '[feedBack]=');
    text = text.replace(/(type|ones|tens)="/g, '[$1]="');

    return text;
  }

  private parseRollbot(original: string): string {
    let text = original.replace('ng-model="', '[value]="');
    text = text.replace('>', ' (valueChange)="answerChanged($event)" >');
    text = text.replace(/\s(|max|min|denominator|direction|editable|numerator)="/g, ' [$1]="');
    text = text.replace(/input=/g, '[input]=');
    text = text.replace(/mode=/g, '[mode]=');
    text = text.replace(/allow-partition-edits=/g, '[allowPartitionEdits]=');
    text = text.replace(/number-line-denominator=/g, '[numberLineDenominator]=');
    text = text.replace(/ship-location=/g, 'shipLocation=');
    text = text.replace(/ship-width=/g, 'shipWidth=');
    text = text.replace(/time-step=/g, 'timeStep=');
    text = text.replace(/initial-gear-count=/g, 'initialGearCount=');

    return text;
  }

  private parseScorable(original: string): string {
    const outputs: string[] = ['(valueChange)="answerChanged($event)"', '(submitAnswer)="submit($event)"'];
    let text = original.replace(/ng-model="/g, '[value]="');
    text = text.replace('>', ` ${outputs.join(' ')} >`);

    return text;
  }

  private parseGraph(original: string): string {
    let text = original.replace(/grid-frame=/g, '[gridFrame]=');
    text = text.replace(/frame=/g, '[frame]=');
    text = text.replace(/origin=/g, '[origin]=');
    text = text.replace(/background-image=/g, '[backgroundImage]=');
    text = text.replace(/hide-grid=/g, '[hideGrid]=');

    return text;
  }

  private parseMinecart(original: string): string {
    let text = original.replace('ng-model="', '[value]="');
    text = text.replace('>', ' (valueChange)="answerChanged($event)" >');
    text = text.replace(/\s(goal)="/g, ' [$1]="');
    text = text.replace(/add-remove=/g, '[addRemove]=');
    text = text.replace(/start-balloons=/g, '[startBalloons]=');
    text = text.replace(/start-sandbags=/g, '[startSandbags]=');
    text = text.replace(/scrollbar-range-max=/g, '[scrollbarRangeMax]=');
    text = text.replace(/scrollbar-range-min=/g, '[scrollbarRangeMin]=');
    text = text.replace(/use-imgs=/g, '[useImgs]=');
    text = text.replace(/term-limit=/g, '[termLimit]=');

    return text;
  }

  private parseMaterialBridge(original: string): string {
    let text = original.replace('ng-model="', '[value]="');
    text = text.replace('>', ' (valueChange)="answerChanged($event)" >');
    text = text.replace(/upper-bridge=/g, '[upperBridge]=');
    text = text.replace(/lower-bridge=/g, '[lowerBridge]=');
    text = text.replace(/fraction-range=/g, '[fractionRange]=');
    text = text.replace(/limits=/g, '[limits]=');

    return text;
  }

  private getStartTagRegExp(tagName: string): RegExp {
    return RegExp(`<\\s*${tagName}[\\s/>]`);
  }

  private getEndTagRegExp(tagName: string): RegExp {
    return RegExp(`<\\s*/\\s*${tagName}\\s*>`);
  }

  private generateTagsDictionary(): Record<string, ParsingComponentConfig> {
    const tags: Record<string, any> = {
      addingBlocks: {
        parserFn: this.parseAddingBlocks.bind(this),
        tagName: 'kh-manip-addingblocks'
      },
      algebraTiles: {
        parserFn: this.parseAlgebraTiles.bind(this),
        tagName: 'kh-manip-algebra-tiles'
      },
      equivFractions: {
        parserFn: this.parseEquivFractions.bind(this),
        tagName: 'kh-manip-equiv-fractions'
      },
      fractionStrips: {
        parserFn: this.parseFractionStrips.bind(this),
        tagName: 'kh-manip-fraction-strips'
      },
      imageSwap: {
        parserFn: this.parseImageSwap.bind(this),
        tagName: 'kh-manip-imageswap'
      },
      integerInput: {
        parserFn: this.parseIntegerInput.bind(this),
        tagName: 'kh-integer-input'
      },
      multiply: {
        parserFn: this.parseMultiply.bind(this),
        tagName: 'kh-manip-multiply'
      },
      dotConnect: {
        tagName: 'kh-manip-dot-connect',
        parserFn: this.parseDotConnect.bind(this)
      },
      subtractingBlocks: {
        parserFn: this.parseSubtractingBlocks.bind(this),
        tagName: 'kh-manip-subtractingblocks'
      },
      spotlight: {
        tagName: 'kh-puzzle-spotlight',
        parserFn: this.parseSpotlight.bind(this)
      },
      khStringInput: {
        parserFn: this.parseStringInput.bind(this),
        tagName: 'kh-string-input'
      },
      imageZoom: {
        tagName: 'kh-image-zoom',
        parserFn: this.parseImageZoom.bind(this)
      },
      representingNumbers: {
        tagName: 'kh-puzzle-representing-numbers',
        parserFn: this.parseRepresentingNumbers.bind(this)
      },
      rollBot: {
        tagName: 'kh-puzzle-rollbot',
        parserFn: this.parseRollbot.bind(this)
      },
      minecart: {
        tagName: 'kh-puzzle-minecart',
        parserFn: this.parseMinecart.bind(this)
      },
      materialBridge: {
        tagName: 'kh-puzzle-material-bridge',
        parserFn: this.parseMaterialBridge.bind(this)
      },
      video: {
        tagName: 'kh-video',
        parserFn: this.parseVideo.bind(this)
      },
      khManipScorable: {
        tagName: 'kh-manipulative',
        parserFn: this.parseScorable.bind(this)
      },
      khManipGraph: {
        tagName: 'kh-manip-graph',
        parserFn: this.parseGraph.bind(this)
      }
    };

    for (const tag of Object.keys(tags)) {
      tags[tag].startTagRegExp = this.getStartTagRegExp(tags[tag].tagName);
      tags[tag].endTagRegExp = this.getEndTagRegExp(tags[tag].tagName);
    }

    return tags as Record<string, ParsingComponentConfig>;
  }
}
